import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import * as fromRoot from '@app/reducers';
import { select, Store } from '@ngrx/store';
import { Observable, ReplaySubject, of } from 'rxjs';
import { map, mergeMap, takeUntil, distinctUntilChanged, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import { AccountActions } from '../actions';
import { CookiesService } from '../services/cookies.service';
import { TokenService } from '../services/token.service';
import { DomainRedirectionGuard } from './base/domain-redirection.guard';
import { isPlatformServer } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class SignupFormGuard  {

  constructor(
    private store$: Store<fromRoot.State>,
    private router: Router,
    private tokenService: TokenService,
    private domainRedirectionGuard: DomainRedirectionGuard,
    private cookiesService: CookiesService,
    @Inject(PLATFORM_ID) private platformId: object
  ) { }

  canActivate(): Observable<boolean> {
    if (isPlatformServer(this.platformId)) {
      return of(true);
    }


    return this.isRootOrNotLoggedIn().pipe(mergeMap((isRootOrNotLoggedIn): Observable<boolean> => {
      if (isRootOrNotLoggedIn) {
        return this.isPaymentRequired().pipe(map((isPaymentRequired): boolean => {
          return isPaymentRequired;
        }));
      } else {
        return this.checkTeamLoaded().pipe(mergeMap((ok: boolean): Observable<boolean> => {
          return this.domainRedirectionGuard.canActivate();
        }));
      }
    }));
  }

  checkTeamLoaded(): Observable<boolean> {
    this.store$.dispatch(AccountActions.LoadUserRolesAndTeams());
    const teamLoaded$ = new ReplaySubject<boolean>();
    this.store$.pipe(
      select(fromRoot.getUserTeams),
      distinctUntilChanged(),
      takeUntil(teamLoaded$),
    ).subscribe((teams: any[]) => {
      if (teams?.length) {
        const lastActiveTeam = this.cookiesService.getCookie('lastActiveTeam');
        const resolvedTeam = teams.find(team => lastActiveTeam?.team === team?.id);
        this.store$.dispatch(AccountActions.updateActiveTeam({ team: resolvedTeam || teams[0] }));
        teamLoaded$.next(true);
      }
    });

    return teamLoaded$.asObservable();
  }

  isRootOrNotLoggedIn(): Observable<boolean> {
    const isRootOrNotLoggedIn$ = new ReplaySubject<boolean>();
    this.store$.pipe(
      select(fromRoot.getToken),
      takeUntil(isRootOrNotLoggedIn$)
    ).subscribe((token) => {
      if (token) {
        if (this.tokenService.isRoot(token) || this.tokenService.isAnonymousAccount(token)) {
          isRootOrNotLoggedIn$.next(true);
        } else {
          if (this.tokenService.isTokenExpired(token)) {
            isRootOrNotLoggedIn$.next(true);
          } else {
            isRootOrNotLoggedIn$.next(false);
          }
        }
      } else {
        isRootOrNotLoggedIn$.next(true);
      }
    });
    return isRootOrNotLoggedIn$.asObservable();
  }

  isPaymentRequired(): Observable<boolean> {
    this.store$.dispatch(AccountActions.loadSubscription());
    const isPaymentRequired$ = new ReplaySubject<boolean>();
    this.store$.pipe(
      select(fromRoot.getSubscription),
      withLatestFrom(
        this.store$.select(fromRoot.getSubscriptionLoaded),
      ),
      takeUntil(isPaymentRequired$),
    ).subscribe(([subscription, subscriptionLoaded]) => {
      if (subscriptionLoaded) {
        if (subscription && subscription.status) {
          switch (subscription.status) {
            case Constant.subscriptionStatus.Active:
              if (subscription.automaticRenew) {
                this.router.navigateByUrl('/account/admin/dashboard');
                isPaymentRequired$.next(false);
              } else { // handle upgrade plan
                isPaymentRequired$.next(true);
              }
              break;
            case Constant.subscriptionStatus.PaymentRequired:
              isPaymentRequired$.next(true);
              break;
            case Constant.subscriptionStatus.Inactive:
              isPaymentRequired$.next(true);
              break;
            default:
              // to fix
              this.router.navigateByUrl('/account/admin/dashboard');
              isPaymentRequired$.next(false);
              break;
          }
        } else {
          isPaymentRequired$.next(true);
        }
      }
    });
    return isPaymentRequired$.asObservable();
  }
}
